import { nextBackoffMs, shouldStopRetrying } from "./backoff";

// The SOCKET's state: connecting (socket created, not open yet), connected (onopen fired),
// disconnected (dropped, waiting out the backoff, or stopped). Whether Cas can answer — a workspace
// started, a Session bound — is the panel's ConnectionPhase (chatPhase.ts), not this.
export type ConnStatus = "connecting" | "connected" | "disconnected";

export interface ControllerDeps {
  makeSocket: (url: string) => WebSocketLike;   // injected; real => (u)=>new WebSocket(u)
  buildUrl: () => string;                        // auth token + claude token query
  now: () => number;
  setTimer: (fn: () => void, ms: number) => number;
  clearTimer: (id: number) => void;
  onStatus: (s: ConnStatus) => void;
  onMessage: (data: any) => void;                // parsed frames (non-pong)
  onConnError?: (reason: string) => void;        // terminal connection failure (retries exhausted / token cleared)
  getExpiry: () => number | null;                // when the current login expires
  refreshToken: () => Promise<boolean>;          // true=refreshed(reconnect), false=cleared
  heartbeatMs?: number;  // default 20_000
  watchdogMs?: number;   // default 10_000
}

export interface WebSocketLike {
  send(d: string): void; close(): void;
  onopen: ((e: any) => any) | null; onclose: ((e: any) => any) | null;
  onerror: ((e: any) => any) | null; onmessage: ((e: any) => any) | null;
  readyState: number;
}

export function createAiConnectionController(deps: ControllerDeps) {
  const HB = deps.heartbeatMs ?? 20_000, WD = deps.watchdogMs ?? 10_000;
  let socket: WebSocketLike | null = null;
  // Epoch is the single source of truth for "which socket is live". Each connect()
  // bumps it and every handler captures the epoch it was bound under; a handler
  // whose epoch is no longer current is a STALE event and is ignored. This makes
  // the whole class of reconnect-storm bugs structurally impossible:
  //   - a failed socket fires both onerror AND onclose → the second is stale
  //   - retryNow()/stop() close the old socket → its onclose is stale
  //   - late onmessage/ping from a superseded socket → stale
  // so only the current socket can ever schedule a reconnect or mutate state.
  let epoch = 0;
  let attempt = 0, manualClosed = false;
  // established: has THIS socket proven stable (received an inbound frame)?
  // firstFailureAt: when the current failure streak began (for the elapsed cap).
  let established = false;
  // The last inbound frame on the current socket: a focus/visibility probe only pings a socket
  // that has been heard from recently; an older one is dropped into the normal reconnect path.
  let lastFrameAt = 0;
  let firstFailureAt: number | null = null;
  let reconnectT: number | null = null, hbT: number | null = null, wdT: number | null = null, refreshT: number | null = null;
  const clearAll = () => { for (const t of [reconnectT, hbT, wdT, refreshT]) if (t != null) deps.clearTimer(t); reconnectT = hbT = wdT = refreshT = null; };
  function connect() {
    stopTimers();
    if (reconnectT != null) { deps.clearTimer(reconnectT); reconnectT = null; }
    const myEpoch = ++epoch;
    established = false;                  // fresh socket is unproven until it receives a frame
    deps.onStatus("connecting");
    const ws = deps.makeSocket(deps.buildUrl()); socket = ws;
    // NB: onopen does NOT reset the backoff. A socket briefly opening then
    // dropping (the leak's flap) is not proof of a healthy connection; only an
    // inbound frame is (see onmessage). Resetting on bare open defeated backoff.
    ws.onopen = () => { if (myEpoch !== epoch) return; deps.onStatus("connected"); lastFrameAt = deps.now(); ws.send(JSON.stringify({ type: "session.list" })); startHeartbeat(); armRefresh(); };
    ws.onmessage = (e) => { if (myEpoch !== epoch) return; lastFrameAt = deps.now(); armWatchdogClear(); if (!established) { established = true; attempt = 0; firstFailureAt = null; } let d: any; try { d = JSON.parse(e.data); } catch { return; } if (d?.type === "pong") return; deps.onMessage(d); };
    ws.onclose = () => onDrop(myEpoch);
    ws.onerror = () => onDrop(myEpoch);
  }
  function onDrop(droppedEpoch: number) {
    if (droppedEpoch !== epoch) return;   // stale socket (double-fire / orphan) → ignore
    if (manualClosed) return;
    epoch++;                              // invalidate this socket's remaining events (incl. the close() below)
    deps.onStatus("disconnected"); stopTimers();
    // Close the dropped socket so the browser stops holding it open (and stops
    // auto-ponging it). Leaving it open kept dead sockets alive and the server
    // never released its holder. The close() re-enters onclose, but the epoch
    // bump above makes that event stale → ignored.
    const dropped = socket; socket = null; dropped?.close();
    const now = deps.now();
    if (firstFailureAt === null) firstFailureAt = now;
    if (shouldStopRetrying(attempt, firstFailureAt, now)) {
      deps.onConnError?.(`Connection lost — gave up after ${attempt} attempts. Click retry to reconnect.`);
      return;
    }
    reconnectT = deps.setTimer(connect, nextBackoffMs(attempt++));
  }
  function startHeartbeat() { hbT = deps.setTimer(function tick() { socket?.send(JSON.stringify({ type: "ping" })); wdT = deps.setTimer(() => socket?.close(), WD); hbT = deps.setTimer(tick, HB); }, HB); }
  function armWatchdogClear() { if (wdT != null) { deps.clearTimer(wdT); wdT = null; } }
  function armRefresh() { const exp = deps.getExpiry(); if (exp == null) return; const fireIn = Math.max(0, exp - 90_000 - deps.now()); refreshT = deps.setTimer(async () => { const ok = await deps.refreshToken(); if (ok) retryNow(); }, fireIn); }
  function stopTimers() { for (const t of [hbT, wdT, refreshT]) if (t != null) deps.clearTimer(t); hbT = wdT = refreshT = null; }
  function retryNow() {
    attempt = 0;
    firstFailureAt = null;                // manual retry starts a fresh retry budget
    established = false;
    epoch++;                              // invalidate current socket: its close() below is now stale
    if (reconnectT != null) { deps.clearTimer(reconnectT); reconnectT = null; }
    stopTimers();
    const old = socket; socket = null;
    old?.close();
    deps.onStatus("disconnected");        // a retry tears the socket down: signal disconnect so
                                          // per-socket state (createdRef) resets before the fresh create
    connect();
  }
  return {
    start() { manualClosed = false; connect(); },
    stop() { manualClosed = true; epoch++; clearAll(); socket?.close(); },
    retryNow,
    /** A visibility/focus/online probe: reconnect now, or health-check a possibly suspended socket. */
    reconnectNow() {
      if (manualClosed) return;
      const now = deps.now();
      if (socket && socket.readyState === 1) {
        if (now - lastFrameAt < HB) {
          socket.send(JSON.stringify({ type: "ping" }));
          if (wdT != null) deps.clearTimer(wdT);
          wdT = deps.setTimer(() => socket?.close(), WD);
        } else {
          socket.close();
        }
        return;
      }
      if (reconnectT != null) { deps.clearTimer(reconnectT); reconnectT = null; }
      connect();
    },
    /** Test-only: close the live socket WITHOUT setting manualClosed, so the
     * normal onclose→backoff-reconnect path runs and the client auto-recovers. */
    dropForTest() { socket?.close(); },
    // true when the frame went out on an open socket. A frame for a socket that is not open is not
    // queued: the caller decides — AiConnectionProvider never marks a session created for a dropped
    // session.create, and says so in the chat (agent-chat-speed Decision 5).
    send(obj: any): boolean {
      if (socket && socket.readyState === 1) { socket.send(JSON.stringify(obj)); return true; }
      return false;
    },
  };
}
